import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Spinner, Table, Badge, Card, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Select from 'react-select'
import { recipeAPI } from '../api/recipeAPI'
import { stockItemAPI } from '../api/stockItemAPI'
import { itemFamilyAPI } from '../api/itemFamilyAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'
import ConfirmationModal from '../components/ConfirmationModal'

// UOM options matching backend UNITS_OF_MEASURE_CHOICES
const UOM_OPTIONS = [
    { value: 'bag', label: 'Bag' },
    { value: 'bulk', label: 'Bulk' },
    { value: 'ct', label: 'Count' },
    { value: 'film', label: 'Film' },
    { value: 'jar', label: 'Jar' },
    { value: 'pallet', label: 'Pallet' },
    { value: 'tote_bag', label: 'Tote Bag' },
]

// Weight unit options matching backend ITEM_SIZE_UNITS_CHOICES
const WEIGHT_UNIT_OPTIONS = [
    { value: 'lbs', label: 'Pounds (lbs)' },
    { value: 'oz', label: 'Ounces (oz)' },
]

// Input mode options
const INPUT_MODE_OPTIONS = [
    { value: 'cases', label: 'Cases' },
    { value: 'sub_units', label: 'Sub-units' },
    { value: 'weight', label: 'Weight' },
]

// UOMs that don't support weight input mode
const NO_WEIGHT_UOMS = ['ct', 'pallet']

// Get sub-unit label based on UOM
const getSubUnitLabel = (uom) => {
    if (uom === 'film') return 'impressions'
    return 'items'
}

// Format quantity display based on input mode
const formatQuantityDisplay = (ingredient) => {
    const qty = parseFloat(ingredient.quantity).toFixed(4)
    const uom = ingredient.unit_of_measure

    if (ingredient.input_mode === 'sub_units' && ingredient.sub_unit_quantity) {
        const subUnits = parseFloat(ingredient.sub_unit_quantity).toFixed(2)
        const subUnitLabel = getSubUnitLabel(uom)
        return `${subUnits} ${subUnitLabel} (${qty} ${uom})`
    } else if (ingredient.input_mode === 'weight' && ingredient.weight_value) {
        return `${ingredient.weight_value} ${ingredient.weight_unit} (${qty} ${uom})`
    }
    return `${qty} ${uom}`
}

function RecipeUpdateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const mode = id ? 'update' : 'create'

    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(mode === 'update')
    const [recipe, setRecipe] = useState(null)

    // Recipe form fields
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [outputStockItem, setOutputStockItem] = useState(null)
    const [yieldQuantity, setYieldQuantity] = useState('1')
    const [isActive, setIsActive] = useState(true)

    // Data for dropdowns
    const [allStockItems, setAllStockItems] = useState([])
    const [allItemFamilies, setAllItemFamilies] = useState([])

    // Ingredient form state
    const [selectedStockItem, setSelectedStockItem] = useState(null)
    const [selectedItemFamily, setSelectedItemFamily] = useState(null)
    const [ingredientQuantity, setIngredientQuantity] = useState('')
    const [ingredientWaste, setIngredientWaste] = useState('0')
    const [ingredientNotes, setIngredientNotes] = useState('')
    const [addingIngredient, setAddingIngredient] = useState(false)
    // Input mode state
    const [inputMode, setInputMode] = useState('cases')
    const [weightValue, setWeightValue] = useState('')
    const [weightUnit, setWeightUnit] = useState(WEIGHT_UNIT_OPTIONS[0]) // Default to lbs

    // Delete modal
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    // Pending ingredients for create mode (stored locally until recipe is created)
    const [pendingIngredients, setPendingIngredients] = useState([])

    // Helper function to get ingredient image
    const getIngredientImage = (ingredient) => {
        if (ingredient.stock_item) {
            // Direct stock item - look up in allStockItems
            const stockItem = allStockItems.find(s => s._id === ingredient.stock_item)
            return stockItem?.image || null
        }
        if (ingredient.item_family) {
            // Item family - find first stock item in this family with an image
            const familyItems = allStockItems.filter(s => s.item_family === ingredient.item_family)
            const itemWithImage = familyItems.find(s => s.image)
            return itemWithImage?.image || null
        }
        return null
    }

    useEffect(() => {
        fetchAllStockItems()
        fetchAllItemFamilies()
        if (mode === 'update') {
            fetchRecipe()
        }
    }, [id, mode])

    const fetchRecipe = async () => {
        try {
            setLoading(true)
            const data = await recipeAPI.getById(id)
            setRecipe(data)
            setName(data.name)
            setDescription(data.description || '')
            setYieldQuantity(data.yield_quantity?.toString() || '1')
            setIsActive(data.is_active)
            // Find and set output stock item
            if (data.output_stock_item) {
                const stockItem = allStockItems.find(s => s._id === data.output_stock_item)
                if (stockItem) {
                    setOutputStockItem(stockItem)
                }
            }
        } catch (error) {
            console.error('Error fetching recipe:', error)
            setError('Failed to load recipe data')
        } finally {
            setLoading(false)
        }
    }

    // Re-set output stock item when allStockItems loads
    useEffect(() => {
        if (recipe && allStockItems.length > 0 && !outputStockItem) {
            const stockItem = allStockItems.find(s => s._id === recipe.output_stock_item)
            if (stockItem) {
                setOutputStockItem(stockItem)
            }
        }
    }, [allStockItems, recipe])

    const fetchAllStockItems = async () => {
        try {
            const response = await stockItemAPI.getAll(1, 1000, '')
            setAllStockItems(response.results || [])
        } catch (error) {
            console.error('Error fetching stock items:', error)
        }
    }

    const fetchAllItemFamilies = async () => {
        try {
            const response = await itemFamilyAPI.getAll(1, 1000, '')
            setAllItemFamilies(response.results || [])
        } catch (error) {
            console.error('Error fetching item families:', error)
        }
    }

    // Filter out stock items already used as ingredients
    const availableStockItems = allStockItems.filter(item => {
        // Check existing ingredients (update mode)
        if (recipe?.ingredients?.some(ing => ing.stock_item === item._id)) return false
        // Check pending ingredients (create mode)
        if (pendingIngredients.some(ing => ing.stock_item === item._id)) return false
        return true
    })

    // Filter out item families already used as ingredients
    const availableItemFamilies = allItemFamilies.filter(family => {
        // Check existing ingredients (update mode)
        if (recipe?.ingredients?.some(ing => ing.item_family === family._id)) return false
        // Check pending ingredients (create mode)
        if (pendingIngredients.some(ing => ing.item_family === family._id)) return false
        return true
    })

    // Handle stock item selection
    const handleStockItemSelect = (stockItem) => {
        setSelectedStockItem(stockItem)
        setSelectedItemFamily(null) // Clear other selection
        if (stockItem) {
            // Set default weight unit based on stock item's item_size_units
            if (stockItem.item_size_units) {
                const weightUnitOption = WEIGHT_UNIT_OPTIONS.find(opt => opt.value === stockItem.item_size_units)
                if (weightUnitOption) {
                    setWeightUnit(weightUnitOption)
                }
            }
            // Reset input mode if current mode is not available for this stock item
            if (stockItem.uom === 'film') {
                // Film only supports sub_units (impressions) and weight
                if (inputMode === 'cases') {
                    setInputMode('sub_units')
                }
            } else {
                if (inputMode === 'sub_units' && stockItem.case_pack === 1) {
                    setInputMode('cases')
                }
                if (inputMode === 'weight' && NO_WEIGHT_UOMS.includes(stockItem.uom)) {
                    setInputMode('cases')
                }
            }
        }
    }

    const handleItemFamilySelect = (itemFamily) => {
        setSelectedItemFamily(itemFamily)
        setSelectedStockItem(null) // Clear other selection
        if (itemFamily) {
            // Set default weight unit based on item family's item_size_units (from first stock item)
            if (itemFamily.item_size_units) {
                const weightUnitOption = WEIGHT_UNIT_OPTIONS.find(opt => opt.value === itemFamily.item_size_units)
                if (weightUnitOption) {
                    setWeightUnit(weightUnitOption)
                }
            }
            // Reset input mode if current mode is not available for this item family
            if (itemFamily.uom === 'film') {
                // Film only supports sub_units (impressions) and weight
                if (inputMode === 'cases') {
                    setInputMode('sub_units')
                }
            } else {
                if (inputMode === 'sub_units' && itemFamily.case_pack === 1) {
                    setInputMode('cases')
                }
                if (inputMode === 'weight' && NO_WEIGHT_UOMS.includes(itemFamily.uom)) {
                    setInputMode('cases')
                }
            }
        }
    }

    const resetIngredientForm = () => {
        setSelectedStockItem(null)
        setSelectedItemFamily(null)
        setIngredientQuantity('')
        setIngredientWaste('0')
        setIngredientNotes('')
        setInputMode('cases')
        setWeightValue('')
        setWeightUnit(WEIGHT_UNIT_OPTIONS[0])
    }

    const submitHandler = async () => {
        if (!name.trim()) {
            setError('Name is required')
            return
        }
        if (!outputStockItem) {
            setError('Output Product is required')
            return
        }

        const recipeData = {
            name,
            description: description || null,
            output_stock_item: outputStockItem._id,
            yield_quantity: parseFloat(yieldQuantity) || 1,
            is_active: isActive
        }

        try {
            if (mode === 'create') {
                // Create the recipe first
                const newRecipe = await recipeAPI.create(recipeData)

                // Add all pending ingredients
                for (const ingredient of pendingIngredients) {
                    const ingredientData = {
                        stock_item: ingredient.stock_item || null,
                        item_family: ingredient.item_family || null,
                        waste_percentage: ingredient.waste_percentage,
                        notes: ingredient.notes || null,
                        input_mode: ingredient.input_mode,
                    }
                    // Add mode-specific fields
                    if (ingredient.input_mode === 'cases') {
                        ingredientData.quantity = ingredient.quantity
                    } else if (ingredient.input_mode === 'sub_units') {
                        ingredientData.sub_unit_quantity_input = ingredient.sub_unit_quantity_input
                    } else if (ingredient.input_mode === 'weight') {
                        ingredientData.weight_value = ingredient.weight_value
                        ingredientData.weight_unit = ingredient.weight_unit
                    }
                    await recipeAPI.addIngredient(newRecipe._id, ingredientData)
                }

                navigate('/recipes')
            } else {
                await recipeAPI.update(id, recipeData)
                navigate('/recipes')
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleAddIngredient = async () => {
        if (!selectedStockItem && !selectedItemFamily) {
            setError('Please select a Stock Item or Item Family')
            return
        }

        // Validate based on input mode
        if (inputMode === 'cases' || inputMode === 'sub_units') {
            if (!ingredientQuantity || parseFloat(ingredientQuantity) <= 0) {
                setError('Quantity is required and must be greater than 0')
                return
            }
        } else if (inputMode === 'weight') {
            if (!weightValue || parseFloat(weightValue) <= 0) {
                setError('Weight is required and must be greater than 0')
                return
            }
        }

        const wastePercentage = parseFloat(ingredientWaste) || 0
        let quantity = 0
        let displayQuantity = ''

        // Get properties from selected stock item or item family
        const selectedItem = selectedStockItem || selectedItemFamily
        const uomValue = selectedItem?.uom || 'cases'
        const uomLabel = UOM_OPTIONS.find(opt => opt.value === uomValue)?.label?.toLowerCase() || uomValue

        // Calculate quantity based on input mode
        if (inputMode === 'cases') {
            quantity = parseFloat(ingredientQuantity)
            displayQuantity = `${quantity} ${uomLabel}${quantity !== 1 ? 's' : ''}`
        } else if (inputMode === 'sub_units') {
            const subUnits = parseFloat(ingredientQuantity)
            const casePack = selectedItem?.case_pack || 1
            quantity = subUnits / casePack
            const subUnitLabel = getSubUnitLabel(selectedItem?.uom)
            displayQuantity = `${subUnits} ${subUnitLabel} (${quantity.toFixed(4)} ${uomLabel}s)`
        } else if (inputMode === 'weight') {
            // For weight mode, we'll let the backend calculate the cases
            // For display, we show the weight value
            displayQuantity = `${weightValue} ${weightUnit.label}`
            // Estimate quantity for display (backend will recalculate)
            if (selectedItem?.item_size && selectedItem?.case_pack) {
                const weightVal = parseFloat(weightValue)
                let weightInItemUnits = weightVal
                // Convert to item's units if different
                if (weightUnit.value !== selectedItem.item_size_units) {
                    if (weightUnit.value === 'oz' && selectedItem.item_size_units === 'lbs') {
                        weightInItemUnits = weightVal / 16
                    } else if (weightUnit.value === 'lbs' && selectedItem.item_size_units === 'oz') {
                        weightInItemUnits = weightVal * 16
                    }
                }
                const items = weightInItemUnits / parseFloat(selectedItem.item_size)
                quantity = items / selectedItem.case_pack
            }
        }

        const actualQuantity = quantity * (1 + wastePercentage / 100)

        // Build ingredient data for API (unit_of_measure is derived from stock_item/item_family)
        const ingredientData = {
            stock_item: selectedStockItem?._id || null,
            item_family: selectedItemFamily?._id || null,
            waste_percentage: wastePercentage,
            notes: ingredientNotes || null,
            input_mode: inputMode,
        }

        // Add mode-specific fields
        if (inputMode === 'cases') {
            ingredientData.quantity = parseFloat(ingredientQuantity)
        } else if (inputMode === 'sub_units') {
            ingredientData.sub_unit_quantity_input = parseFloat(ingredientQuantity)
        } else if (inputMode === 'weight') {
            ingredientData.weight_value = parseFloat(weightValue)
            ingredientData.weight_unit = weightUnit.value
        }

        // For display purposes in create mode
        const displayData = {
            ...ingredientData,
            ingredient_name: selectedStockItem?.description || selectedItemFamily?.name,
            quantity: quantity,
            actual_quantity: actualQuantity,
            display_quantity: displayQuantity,
            _tempId: Date.now() // Temporary ID for local state management
        }

        if (mode === 'create') {
            // Add to local pending list
            setPendingIngredients([...pendingIngredients, displayData])
            resetIngredientForm()
            setError(null)
        } else {
            // Update mode - call API
            try {
                setAddingIngredient(true)
                const updatedRecipe = await recipeAPI.addIngredient(id, ingredientData)
                setRecipe(updatedRecipe)
                resetIngredientForm()
                setError(null)
            } catch (error) {
                console.error('Error adding ingredient:', error)
                setError(`Failed to add ingredient: ${error.response?.data?.error || error.message}`)
            } finally {
                setAddingIngredient(false)
            }
        }
    }

    const handleRemoveIngredient = async (ingredientId) => {
        try {
            const updatedRecipe = await recipeAPI.deleteIngredient(id, ingredientId)
            setRecipe(updatedRecipe)
        } catch (error) {
            console.error('Error removing ingredient:', error)
            alert(`Failed to remove ingredient: ${error.response?.data?.error || error.message}`)
        }
    }

    const handleRemovePendingIngredient = (tempId) => {
        setPendingIngredients(pendingIngredients.filter(ing => ing._tempId !== tempId))
    }

    const handleDeleteClick = () => {
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        try {
            await recipeAPI.delete(id)
            navigate('/recipes')
        } catch (error) {
            console.error('Error deleting recipe:', error)
            alert(`Failed to delete recipe: ${error.response?.data?.message || error.message}`)
        } finally {
            setShowDeleteModal(false)
        }
    }

    if (loading || permissionsLoading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    // For create mode, require recipes.modify permission
    // For update mode, allow viewing with recipes.view, but disable editing without recipes.modify
    if (mode === 'create' && !hasPermission('recipes.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to create recipes.
                </Alert>
            </Container>
        )
    }

    const canModify = hasPermission('recipes.modify')

    if (mode === 'update' && !recipe) {
        return (
            <Container className="py-4">
                <Alert variant="warning">Recipe not found</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/recipes')}>
                    Back to List
                </Button>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <h1>{mode === 'create' ? 'Create Recipe' : (canModify ? 'Update Recipe' : 'Recipe Details')}</h1>

                    {/* Recipe Basic Info */}
                    <Form.Group className="mb-4" controlId="name">
                        <Form.Label>Name*</Form.Label>
                        <Form.Control
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            placeholder="Enter recipe name"
                            autoComplete="off"
                            spellCheck="false"
                            disabled={!canModify}
                        />
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="outputStockItem">
                        <Form.Label>Output Product*</Form.Label>
                        <div className="d-flex align-items-center">
                            {outputStockItem?.image && (
                                <img
                                    src={outputStockItem.image}
                                    alt={outputStockItem.description}
                                    style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                    className="me-3"
                                />
                            )}
                            <div className="flex-grow-1">
                                <Select
                                    value={outputStockItem}
                                    onChange={setOutputStockItem}
                                    options={allStockItems.filter(s => s.is_manufactured)}
                                    getOptionLabel={option => option.description}
                                    getOptionValue={option => option._id}
                                    filterOption={(option, inputValue) => {
                                        if (!inputValue) return true
                                        const searchTerm = inputValue.toLowerCase()
                                        const description = option.data.description?.toLowerCase() || ''
                                        return description.includes(searchTerm)
                                    }}
                                    placeholder="Select output product..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable={true}
                                    isSearchable={true}
                                    isDisabled={!canModify}
                                />
                            </div>
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="yieldQuantity">
                        <Form.Label>Yield Quantity*</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={yieldQuantity}
                            onChange={(e) => setYieldQuantity(e.target.value)}
                            required
                            disabled={!canModify}
                        />
                        <Form.Text className="text-muted">
                            Units produced per batch
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="description">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional description"
                            disabled={!canModify}
                        />
                    </Form.Group>

                </Col>
            </Row>

            {/* Ingredients Section - show in both create and update modes */}
            <h4>Ingredients</h4>

            {/* Add Ingredient Card - only show if user can modify */}
            {canModify && (
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <div className="card mb-3">
                        <div className="card-header bg-body-secondary">
                            <h5 className="mb-0">Add Ingredient</h5>
                        </div>
                        <div className="card-body">
                            <Form.Group className="mb-3" controlId="stockItemSelect">
                                <Form.Label>Stock Item</Form.Label>
                                <Select
                                    value={selectedStockItem}
                                    onChange={handleStockItemSelect}
                                    options={availableStockItems}
                                    getOptionLabel={option => `${option.description}${option.gtin ? ` (${option.gtin})` : ''}`}
                                    getOptionValue={option => option._id}
                                    filterOption={(option, inputValue) => {
                                        if (!inputValue) return true
                                        const searchTerm = inputValue.toLowerCase()
                                        const description = option.data.description?.toLowerCase() || ''
                                        const gtin = option.data.gtin?.toLowerCase() || ''
                                        return description.includes(searchTerm) || gtin.includes(searchTerm)
                                    }}
                                    placeholder="Search by name or GTIN..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable={true}
                                    isSearchable={true}
                                    isDisabled={selectedItemFamily !== null}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="itemFamilySelect">
                                <Form.Label>Item Family</Form.Label>
                                <Select
                                    value={selectedItemFamily}
                                    onChange={handleItemFamilySelect}
                                    options={availableItemFamilies}
                                    getOptionLabel={option => option.name}
                                    getOptionValue={option => option._id}
                                    filterOption={(option, inputValue) => {
                                        if (!inputValue) return true
                                        return option.data.name.toLowerCase().includes(inputValue.toLowerCase())
                                    }}
                                    placeholder="Or select item family..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable={true}
                                    isSearchable={true}
                                    isDisabled={selectedStockItem !== null}
                                />
                                <Form.Text className="text-muted">
                                    Use item family for flexible ingredient substitution
                                </Form.Text>
                            </Form.Group>

                            {/* Input Mode Selector - show when stock item or item family is selected */}
                            {(selectedStockItem || selectedItemFamily) && (() => {
                                // Get properties from selected stock item or item family
                                const selectedItem = selectedStockItem || selectedItemFamily
                                return (
                                    <Form.Group className="mb-3" controlId="inputMode">
                                        <Form.Label>Input Mode</Form.Label>
                                        <div>
                                            {INPUT_MODE_OPTIONS.map((option) => {
                                                // For film: hide cases, show only sub_units (impressions) and weight
                                                if (selectedItem?.uom === 'film') {
                                                    if (option.value === 'cases') {
                                                        return null
                                                    }
                                                } else {
                                                    // Hide sub_units if case_pack is 1
                                                    if (option.value === 'sub_units' && selectedItem?.case_pack === 1) {
                                                        return null
                                                    }
                                                    // Hide weight for certain UOMs
                                                    if (option.value === 'weight' && NO_WEIGHT_UOMS.includes(selectedItem?.uom)) {
                                                        return null
                                                    }
                                                }
                                                // Custom label for film sub_units
                                                const label = (option.value === 'sub_units' && selectedItem?.uom === 'film')
                                                    ? 'Impressions'
                                                    : option.label
                                                return (
                                                    <Form.Check
                                                        key={option.value}
                                                        inline
                                                        type="radio"
                                                        id={`inputMode-${option.value}`}
                                                        label={label}
                                                        name="inputMode"
                                                        value={option.value}
                                                        checked={inputMode === option.value}
                                                        onChange={(e) => setInputMode(e.target.value)}
                                                    />
                                                )
                                            })}
                                        </div>
                                        {inputMode === 'sub_units' && (
                                            <Form.Text className="text-muted">
                                                Case pack: {selectedItem.case_pack} {getSubUnitLabel(selectedItem.uom)} per {UOM_OPTIONS.find(opt => opt.value === selectedItem.uom)?.label?.toLowerCase() || 'case'}
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                )
                            })()}

                            <Row className="mb-3">
                                {/* Quantity input - shown for cases and sub_units modes */}
                                {(inputMode === 'cases' || inputMode === 'sub_units') && (() => {
                                    const selectedItem = selectedStockItem || selectedItemFamily
                                    return (
                                        <Col>
                                            <Form.Group controlId="ingredientQuantity">
                                                <Form.Label>
                                                    {inputMode === 'cases' ? 'Quantity (cases)*' : `Quantity (${getSubUnitLabel(selectedItem?.uom)})*`}
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.0001"
                                                    min="0"
                                                    value={ingredientQuantity}
                                                    onChange={(e) => setIngredientQuantity(e.target.value)}
                                                    placeholder="0.00"
                                                />
                                                {inputMode === 'sub_units' && ingredientQuantity && selectedItem?.case_pack && (
                                                    <Form.Text className="text-muted">
                                                        = {(parseFloat(ingredientQuantity) / selectedItem.case_pack).toFixed(4)} {UOM_OPTIONS.find(opt => opt.value === selectedItem.uom)?.label?.toLowerCase() || 'cases'}
                                                    </Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    )
                                })()}

                                {/* Weight inputs - shown for weight mode */}
                                {inputMode === 'weight' && (
                                    <>
                                        <Col>
                                            <Form.Group controlId="weightValue">
                                                <Form.Label>Weight*</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.001"
                                                    min="0"
                                                    value={weightValue}
                                                    onChange={(e) => setWeightValue(e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={3}>
                                            <Form.Group controlId="weightUnit">
                                                <Form.Label>Unit</Form.Label>
                                                <Select
                                                    value={weightUnit}
                                                    onChange={setWeightUnit}
                                                    options={WEIGHT_UNIT_OPTIONS}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    isClearable={false}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </>
                                )}

                                {/* UOM display - derived from stock item or item family */}
                                {(selectedStockItem || selectedItemFamily) && (() => {
                                    const selectedItem = selectedStockItem || selectedItemFamily
                                    return (
                                        <Col xs={2}>
                                            <Form.Group controlId="ingredientUom">
                                                <Form.Label>UOM</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={UOM_OPTIONS.find(opt => opt.value === selectedItem.uom)?.label || selectedItem.uom}
                                                    disabled
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Col>
                                    )
                                })()}
                                <Col>
                                    <Form.Group controlId="ingredientWaste">
                                        <Form.Label>Waste %</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            value={ingredientWaste}
                                            onChange={(e) => setIngredientWaste(e.target.value)}
                                            placeholder="0"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Notes field - commented out for future use
                            <Form.Group className="mb-3" controlId="ingredientNotes">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={ingredientNotes}
                                    onChange={(e) => setIngredientNotes(e.target.value)}
                                    placeholder="Optional notes"
                                />
                            </Form.Group>
                            */}

                            <div className="text-end">
                                <Button
                                    variant="success"
                                    onClick={handleAddIngredient}
                                    disabled={
                                        (!selectedStockItem && !selectedItemFamily) ||
                                        addingIngredient ||
                                        ((inputMode === 'cases' || inputMode === 'sub_units') && !ingredientQuantity) ||
                                        (inputMode === 'weight' && !weightValue)
                                    }
                                >
                                    {addingIngredient ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            Adding...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-plus me-2"></i>
                                            Add Ingredient
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            )}

            {/* Ingredients Table - show pending ingredients in create mode, or saved ingredients in update mode */}
            {mode === 'create' ? (
                pendingIngredients.length > 0 ? (
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>Image</th>
                                <th>Type</th>
                                <th>Ingredient</th>
                                <th>Quantity</th>
                                <th>Waste %</th>
                                <th>Actual Qty</th>
                                {/* <th>Notes</th> */}
                                <th style={{ width: '100px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingIngredients.map((ingredient) => (
                                <tr key={ingredient._tempId}>
                                    <td>
                                        {getIngredientImage(ingredient) ? (
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        <img
                                                            src={getIngredientImage(ingredient)}
                                                            alt="Ingredient"
                                                            style={{
                                                                width: '300px',
                                                                height: '300px',
                                                                objectFit: 'contain',
                                                                border: '2px solid #122A5A',
                                                                borderRadius: '4px',
                                                                boxShadow: '0 4px 8px rgba(18, 42, 90, 0.1)',
                                                                transition: 'transform 0.2s ease-in-out',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                }
                                            >
                                                <img
                                                    src={getIngredientImage(ingredient)}
                                                    alt="Ingredient Thumbnail"
                                                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                />
                                            </OverlayTrigger>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg={ingredient.stock_item ? 'primary' : 'info'}>
                                            {ingredient.stock_item ? 'Stock Item' : 'Item Family'}
                                        </Badge>
                                    </td>
                                    <td>{ingredient.ingredient_name}</td>
                                    <td>{ingredient.display_quantity || `${parseFloat(ingredient.quantity).toFixed(4)} ${ingredient.unit_of_measure}`}</td>
                                    <td>{ingredient.waste_percentage}%</td>
                                    <td>{parseFloat(ingredient.actual_quantity).toFixed(4)}</td>
                                    {/* <td>{ingredient.notes || '-'}</td> */}
                                    <td>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleRemovePendingIngredient(ingredient._tempId)}
                                        >
                                            Remove
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : null
            ) : (
                recipe?.ingredients && recipe.ingredients.length > 0 ? (
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>Image</th>
                                <th>Type</th>
                                <th>Ingredient</th>
                                <th>Quantity</th>
                                <th>Waste %</th>
                                <th>Actual Qty</th>
                                {/* <th>Notes</th> */}
                                {canModify && <th style={{ width: '100px' }}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {recipe.ingredients.map((ingredient) => (
                                <tr key={ingredient._id}>
                                    <td>
                                        {getIngredientImage(ingredient) ? (
                                            <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                    <Tooltip>
                                                        <img
                                                            src={getIngredientImage(ingredient)}
                                                            alt="Ingredient"
                                                            style={{
                                                                width: '300px',
                                                                height: '300px',
                                                                objectFit: 'contain',
                                                                border: '2px solid #122A5A',
                                                                borderRadius: '4px',
                                                                boxShadow: '0 4px 8px rgba(18, 42, 90, 0.1)',
                                                                transition: 'transform 0.2s ease-in-out',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                }
                                            >
                                                <img
                                                    src={getIngredientImage(ingredient)}
                                                    alt="Ingredient Thumbnail"
                                                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                />
                                            </OverlayTrigger>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg={ingredient.stock_item ? 'primary' : 'info'}>
                                            {ingredient.stock_item ? 'Stock Item' : 'Item Family'}
                                        </Badge>
                                    </td>
                                    <td>{ingredient.ingredient_name || ingredient.stock_item_name || ingredient.item_family_name}</td>
                                    <td>{formatQuantityDisplay(ingredient)}</td>
                                    <td>{ingredient.waste_percentage}%</td>
                                    <td>{parseFloat(ingredient.actual_quantity).toFixed(4)}</td>
                                    {/* <td>{ingredient.notes || '-'}</td> */}
                                    {canModify && (
                                        <td>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleRemoveIngredient(ingredient._id)}
                                            >
                                                Remove
                                            </Button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : null
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible className="mt-4">
                    {error}
                </Alert>
            )}

            <div className="mt-4">
                {mode === 'create' ? (
                    <Button
                        variant="success"
                        className="me-2"
                        onClick={submitHandler}
                    >
                        Create Recipe
                    </Button>
                ) : canModify && (
                    <>
                        <Button
                            variant="success"
                            className="me-2"
                            onClick={submitHandler}
                        >
                            Update
                        </Button>
                        <Button
                            variant="danger"
                            className="me-2"
                            onClick={handleDeleteClick}
                        >
                            Delete
                        </Button>
                    </>
                )}
                <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/recipes')}
                >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to List
                </Button>
            </div>

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message={`Are you sure you want to delete the recipe "${recipe?.name}"? All ingredients will also be deleted.`}
            />
        </Container>
    )
}

export default RecipeUpdateScreen
